import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { clockSeconds, fullTime } from '../../../shared/lib/format';
import { GROUP_CLASS, httpCodeGroup } from '../../../shared/status/dictionary';
import { Button } from '../../../shared/ui/button';
import { SectionPanel } from '../../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { ShortId, Table, type IColumn } from '../../../shared/ui/table';
import {
  issueRuns,
  type TIssueCopy,
  type TIssueDetail,
  type TIssueRun,
} from '../../../entities/issue';
import { EMPTY_RUN_FILTERS, runFiltersToSearch } from '../../../entities/run';

const PAGE = 10;
// A copy is a failure only while it stays one: auto-retried or redelivered ones got through.
const FAILING: readonly string[] = ['failed', 'retrying'];
const count = (value: number) => value.toLocaleString('en-US');
// The Events list's widest period: an issue's runs can be weeks older than its last failure.
const LIST_MINUTES = 30 * 24 * 60;

// The step this issue is about, not the copy's last step: a copy the error branch carried on
// ended on 200 from Telegram, and its 503 at Deliver is what this list is for.
const attempts = (copy: TIssueCopy): string => {
  const count = copy.failure?.attempts ?? copy.attemptCount;
  const tries = count === 1 ? '1 attempt' : `${count.toLocaleString('en-US')} attempts`;
  return copy.recoveredAt ? `${tries} · then the error branch took it` : tries;
};

interface ICopyRow {
  run: TIssueRun;
  copy: TIssueCopy;
}

const codeOf = ({ copy }: ICopyRow) =>
  copy.failure ? copy.failure.responseStatus : copy.responseStatus;

const copyColumns = (timeZone?: string): IColumn<ICopyRow>[] => [
  {
    key: 'id',
    header: 'Copy',
    width: 'w-col-id text-primary',
    render: ({ copy }) => <ShortId id={copy.id} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 'w-col-code',
    render: (row) => {
      const code = codeOf(row);
      return (
        <span
          className={
            code === null || code === undefined ? 'text-faint' : GROUP_CLASS[httpCodeGroup(code)]
          }
          title={row.copy.reason ?? undefined}
        >
          {code ?? '—'}
        </span>
      );
    },
  },
  {
    key: 'what',
    header: 'What happened',
    width: 'grow',
    ui: true,
    render: (row) => (
      <span className="text-meta text-muted">
        {row.copy.reason && (codeOf(row) === null || codeOf(row) === undefined)
          ? row.copy.reason
          : attempts(row.copy)}
      </span>
    ),
  },
  {
    key: 'at',
    header: 'When',
    width: 'w-col-time text-primary',
    align: 'right',
    render: ({ copy }) => (
      <span title={fullTime(copy.at, timeZone)}>{clockSeconds(copy.at, timeZone)}</span>
    ),
  },
];

// The copies of the runs this issue counted, newest first; a run can hold more than one.
export const FailedCopies = ({ issue, timeZone }: { issue: TIssueDetail; timeZone?: string }) => {
  const runs = useInfiniteQuery({
    queryKey: ['issue-runs', issue.id],
    queryFn: ({ pageParam }) => issueRuns(issue, pageParam, PAGE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // Only copies still failed (or failing now) are listed; the server counts the rest.
  const copies = (runs.data?.pages ?? [])
    .flatMap((page) => page.data)
    .flatMap((run) =>
      run.copies
        .filter((copy) => copy.issueId === issue.id && FAILING.includes(copy.status))
        .map((copy) => ({ run, copy })),
    );
  const outcomes = issue.copyOutcomes;
  const failing = outcomes ? outcomes.failed + outcomes.retrying : null;
  const recovered = outcomes
    ? [
        outcomes.retriedThrough > 0 &&
          `${count(outcomes.retriedThrough)} got through on an automatic retry`,
        outcomes.redelivered > 0 && `${count(outcomes.redelivered)} redelivered and got through`,
        outcomes.errorBranch > 0 && `${count(outcomes.errorBranch)} carried on by the error branch`,
      ].filter((part): part is string => typeof part === 'string')
    : [];
  const search = (copyStatus: string[]) => ({
    ...runFiltersToSearch({
      ...EMPTY_RUN_FILTERS,
      issueId: [issue.id],
      copyStatus,
      minutes: LIST_MINUTES,
    }),
  });
  const everySearch = search([]);

  return (
    <SectionPanel
      title="Failed copies"
      note={
        failing === null
          ? `${count(issue.eventCount)} counted by this issue`
          : `${count(failing)} of the ${count(issue.eventCount)} this issue counted`
      }
      actions={
        <Link to="/events" search={everySearch} className="text-meta text-primary underline">
          See them all in Events
        </Link>
      }
    >
      {runs.isError && (
        <div className="p-input">
          <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
        </div>
      )}
      {runs.isPending && (
        <div className="p-input">
          <LoadingState rows={3} />
        </div>
      )}
      {runs.isSuccess && copies.length === 0 && (
        <p className="px-input py-3 text-data text-muted">
          {recovered.length > 0
            ? 'None left failed: every copy this issue counted got through in the end.'
            : 'None of them is still kept: retention has removed the copies this issue counted.'}
        </p>
      )}
      {recovered.length > 0 && (
        <p className="border-b border-border-row px-input py-2 text-meta text-muted">
          Not listed: {recovered.join(', ')}.{' '}
          <Link
            to="/events"
            search={search(['completed', 'running', 'waiting'])}
            className="text-primary underline"
          >
            See them
          </Link>
        </p>
      )}
      {copies.length > 0 && (
        <Table
          layoutId="issue-failed-copies"
          columns={copyColumns(timeZone)}
          rows={copies}
          rowKey={({ copy }) => copy.id}
          rowClassName={() => 'hover:bg-hover-raised'}
          wrapRow={({ run, copy }, drawn) => (
            <Link
              to="/events/runs/$runId"
              params={{ runId: run.id }}
              search={{
                at: run.at,
                copy: copy.id,
                step: copy.failure?.node.key ?? copy.node?.key,
              }}
              className="block"
            >
              {drawn}
            </Link>
          )}
        />
      )}
      {runs.hasNextPage && (
        <div className="px-input py-2">
          <Button
            size="sm"
            variant="ghost"
            waiting={runs.isFetchingNextPage}
            waitingLabel="Loading"
            onClick={() => void runs.fetchNextPage()}
          >
            Show older ones
          </Button>
        </div>
      )}
    </SectionPanel>
  );
};
